import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CreditCard, Banknote, Save, Loader2, AlertTriangle, ArrowRightLeft } from 'lucide-react';
import api from '@/lib/api';

export default function OwnerPaymentSettings() {
    const [config, setConfig] = useState(null);
    const [methods, setMethods] = useState({
        mercadopago: { enabled: false, globally_enabled: false },
        cash: { enabled: false, globally_enabled: false },
        transfer: { enabled: false, globally_enabled: false },
    });
    const [transferDetails, setTransferDetails] = useState('');
    const [transferActive, setTransferActive] = useState(false);
    const [savingTransfer, setSavingTransfer] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [token, setToken] = useState('');
    const [publicKey, setPublicKey] = useState('');
    const [isActive, setIsActive] = useState(false);

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        setLoading(true);
        try {
            const res = await api.get('/owner/payment-config');
            const data = res.data.data;
            setConfig(data);
            setMethods(data.methods || {
                mercadopago: { enabled: false, globally_enabled: false },
                cash: { enabled: false, globally_enabled: false },
                transfer: { enabled: false, globally_enabled: false },
            });

            if (data.mercadopago_config) {
                setToken(data.mercadopago_config.access_token || '');
                setPublicKey(data.mercadopago_config.public_key || '');
                setIsActive(data.mercadopago_config.is_active || false);
            }

            if (data.transfer_config) {
                setTransferDetails(data.transfer_config.details || '');
                setTransferActive(data.transfer_config.is_active || false);
            }
        } catch {
            // silent
        } finally {
            setLoading(false);
        }
    };

    const saveConfig = async () => {
        setSaving(true);
        try {
            await api.post('/owner/payment-config', {
                access_token: token,
                public_key: publicKey,
                is_active: isActive,
            });
        } catch {
            // silent
        } finally {
            setSaving(false);
        }
    };

    const toggleMethod = async (method, enabled) => {
        try {
            await api.put(`/owner/payment-methods/${method}`, { is_enabled: enabled });
            setMethods(prev => ({
                ...prev,
                [method]: { ...prev[method], enabled },
            }));
        } catch (err) {
            if (err.response?.status === 403) {
                alert(err.response.data.message);
            }
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
        );
    }

    return (
        <div className="space-y-6 max-w-3xl">
            <div>
                <h1 className="text-2xl font-bold uppercase tracking-wide">Configuración de Pagos</h1>
                <p className="text-white/60 text-sm mt-1">Configura los métodos de pago disponibles para tus gimnasios.</p>
            </div>

            {/* Payment Methods */}
            <Card className="border-hairline-dark bg-[#0a0a0a]">
                <CardHeader>
                    <CardTitle className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                        <Banknote className="h-4 w-4" />
                        Métodos de Pago Disponibles
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* MercadoPago */}
                    <div className="flex items-center justify-between py-2">
                        <div className="flex items-center gap-3">
                            <CreditCard className="h-5 w-5 text-white/80" />
                            <div>
                                <p className="text-sm font-medium text-white">MercadoPago</p>
                                <p className="text-xs text-white/60">Pagos online con tarjeta de crédito/débito</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {!methods.mercadopago.globally_enabled && (
                                <Badge variant="outline" className="text-xs border-white/30 text-white/60">
                                    <AlertTriangle className="h-3 w-3 mr-1" />
                                    Deshabilitado globalmente
                                </Badge>
                            )}
                            <Switch
                                checked={methods.mercadopago.enabled}
                                onCheckedChange={(v) => toggleMethod('mercadopago', v)}
                                disabled={!methods.mercadopago.globally_enabled}
                            />
                        </div>
                    </div>
                    <Separator className="bg-white/10" />
                    {/* Cash */}
                    <div className="flex items-center justify-between py-2">
                        <div className="flex items-center gap-3">
                            <Banknote className="h-5 w-5 text-white/80" />
                            <div>
                                <p className="text-sm font-medium text-white">Pago en Efectivo</p>
                                <p className="text-xs text-white/60">Pagos presenciales en el gimnasio</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {!methods.cash.globally_enabled && (
                                <Badge variant="outline" className="text-xs border-white/30 text-white/60">
                                    <AlertTriangle className="h-3 w-3 mr-1" />
                                    Deshabilitado globalmente
                                </Badge>
                            )}
                    <Switch
                        checked={methods.cash.enabled}
                        onCheckedChange={(v) => toggleMethod('cash', v)}
                        disabled={!methods.cash.globally_enabled}
                    />
                </div>
            </div>
            <Separator className="bg-white/10" />
            {/* Transfer */}
            <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3">
                    <ArrowRightLeft className="h-5 w-5 text-white/80" />
                    <div>
                        <p className="text-sm font-medium text-white">Transferencia Bancaria</p>
                        <p className="text-xs text-white/60">Pago por transferencia bancaria</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {!methods.transfer.globally_enabled && (
                        <Badge variant="outline" className="text-xs border-white/30 text-white/60">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Deshabilitado globalmente
                        </Badge>
                    )}
                    <Switch
                        checked={methods.transfer.enabled}
                        onCheckedChange={(v) => toggleMethod('transfer', v)}
                        disabled={!methods.transfer.globally_enabled}
                    />
                </div>
            </div>
        </CardContent>
    </Card>

            {/* MercadoPago Credentials */}
            <Card className="border-hairline-dark bg-[#0a0a0a]">
                <CardHeader>
                    <CardTitle className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                        <CreditCard className="h-4 w-4" />
                        Credenciales de MercadoPago
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                        <Badge variant="default" className="uppercase tracking-wider text-xs bg-white text-black">Producción</Badge>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-white/60">Activo</span>
                            <Switch checked={isActive} onCheckedChange={setIsActive} />
                        </div>
                    </div>
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs text-white/60 uppercase tracking-wider block mb-1.5">Access Token</label>
                            <Input
                                type="password"
                                value={token}
                                onChange={(e) => setToken(e.target.value)}
                                placeholder="Access Token de producción..."
                                className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-white/60 uppercase tracking-wider block mb-1.5">Public Key</label>
                            <Input
                                type="password"
                                value={publicKey}
                                onChange={(e) => setPublicKey(e.target.value)}
                                placeholder="Public Key de producción..."
                                className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark"
                            />
                        </div>
                    </div>
                    <Button
                        onClick={saveConfig}
                        disabled={saving}
                        className="w-full uppercase tracking-widest text-xs"
                    >
                        {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        <Save className="h-4 w-4 mr-2" />
                        Guardar Credenciales
                    </Button>
            <p className="text-xs text-white/40">
                Estas credenciales se usarán para recibir pagos de tus alumnos. Asegúrate de usar las credenciales de producción de tu cuenta de MercadoPago.
            </p>
        </CardContent>
    </Card>

    {/* Transfer config */}
    <Card className="border-hairline-dark bg-[#0a0a0a]">
        <CardHeader>
            <CardTitle className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                <ArrowRightLeft className="h-4 w-4" />
                Datos de Transferencia Bancaria
            </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
                <Badge variant="default" className="uppercase tracking-wider text-xs bg-white text-black">Configuración</Badge>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-white/60">Activo</span>
                    <Switch checked={transferActive} onCheckedChange={setTransferActive} />
                </div>
            </div>
            <div className="space-y-3">
                <div>
                    <label className="text-xs text-white/60 uppercase tracking-wider block mb-1.5">Datos bancarios / Alias / CBU</label>
                    <textarea
                        value={transferDetails}
                        onChange={(e) => setTransferDetails(e.target.value)}
                        placeholder="CBU: 1234567890123456789012&#10;Alias: TU.GIMNASIO&#10;Titular: Juan Perez"
                        rows={4}
                        className="flex min-h-[80px] w-full rounded-md border border-hairline-dark bg-[#0a0a0a] px-3 py-2 text-sm text-white placeholder:text-white/30 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white"
                    />
                </div>
            </div>
            <Button
                onClick={async () => {
                    setSavingTransfer(true);
                    try {
                        await api.post('/owner/transfer-config', { details: transferDetails, is_active: transferActive });
                    } catch {} finally { setSavingTransfer(false); }
                }}
                disabled={savingTransfer}
                className="w-full uppercase tracking-widest text-xs"
            >
                {savingTransfer && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <Save className="h-4 w-4 mr-2" />
                Guardar Configuración
            </Button>
            <p className="text-xs text-white/40">
                Estos datos se mostrarán a los alumnos cuando seleccionen transferencia como método de pago.
            </p>
        </CardContent>
    </Card>
</div>
    );
}
